import { Component, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Firestore, collection, getDocs, doc, updateDoc, Unsubscribe } from '@angular/fire/firestore';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { Subscription, firstValueFrom } from 'rxjs';
import { StoreService } from '../services/store.service';
import { CompanyService } from '../services/company.service';
import { BrandService } from '../services/brand.service';
import { PhotoboothService } from '../services/photobooth.service';
import { AccessService } from '../services/access.service';
import { CoinsingReleaseService, CoinSingReleaseOutcome } from '../services/coinsing-release.service';
import { Brand } from '../models/brand.model';
import { resolveKonoboxBrand } from './konobox-brand.util';
import { KonoboxDeviceService, DeviceIdTakenError, DeviceVerifyError } from './konobox-device.service';
import {
  computeStatus,
  formatLastSeenPacific,
  compareLastSeen,
  normalizePingIntervalMs
} from './konobox-status.util';
import { compareInstalledVersionsForSort, describeInstalledVersion, InstalledVersionView } from '../utils/coinsing-version.util';
import { computeCoinSingUpdatePill, CoinSingPillResult } from '../utils/coinsing-update-pill.util';
import { KonoboxDeviceReleaseModalComponent } from './device-release-modal/konobox-device-release-modal.component';
import { KonoboxAppVersionsModalComponent } from './app-versions-modal/konobox-app-versions-modal.component';
import { KonoboxReleaseHistoryModalComponent } from './release-history-modal/konobox-release-history-modal.component';
import { KonoboxRemoteControlModalComponent } from './remote-control-modal/konobox-remote-control-modal.component';

export interface KonoboxDevice {
  deviceId: string;
  name?: string;
  deviceName?: string;
  storeId?: string;
  storeDocId?: string;
  storeName?: string;
  storeLocation?: string;
  brandId?: string;
  brandName?: string;
  isTesting?: boolean;
  isDeleted?: boolean;
  lastSeen?: any;
  lastPingedDate?: Date | null;
  deviceOnline?: boolean;
  cardMachineConnected?: boolean;
  billAcceptorConnected?: boolean;
  useCard?: boolean;
  useCash?: boolean;
  useCoupon?: boolean;
  isInMaintenance?: boolean;
  appVersion?: string;
  coinsingTargetVersion?: string;
  coinsingUpdateNeeded?: boolean;
  coinsingUpdate?: any;
  overallStatus?: 'online' | 'warning' | 'offline';
  [key: string]: any;
}

export interface StoreOption {
  id: string;
  storeId: string;
  name: string;
  location?: string;
  brandId?: string;
  companyIds?: string[];
}

@Component({
  selector: 'app-konobox-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    KonoboxDeviceReleaseModalComponent,
    KonoboxAppVersionsModalComponent,
    KonoboxReleaseHistoryModalComponent,
    KonoboxRemoteControlModalComponent
  ],
  templateUrl: './konobox-management.component.html',
  styleUrls: ['./konobox-management.component.css']
})
export class KonoboxManagementComponent implements OnInit, OnDestroy {
  private firestore = inject(Firestore);
  private storeService = inject(StoreService);
  private brandService = inject(BrandService);
  private companyService = inject(CompanyService);
  private photoboothService = inject(PhotoboothService);
  private deviceService = inject(KonoboxDeviceService);
  private accessService = inject(AccessService, { optional: true });
  private auth = inject(Auth, { optional: true });
  private releaseService = inject(CoinsingReleaseService, { optional: true });

  isLoading = true;

  // Konobox brand: the `brands` document named (or with the ID) "Konobox". No fallback ID (KAN-163 Part C).
  konoboxBrand: Brand | null = null;
  brandsLoading = true;
  brandLoadError = false;
  private brandSub?: Subscription;
  private deviceSub?: Unsubscribe;
  private authUnsubscribe?: () => void;
  private toastTimer: any;
  toastMessage = '';

  readonly msgBrandNotFound = 'Konobox brand not found. Add Device is disabled until a brand named "Konobox" exists in Brands. Contact an administrator.';
  readonly msgBrandLoadFailed = 'Brands could not be loaded. Device list and Add Device are unavailable. Reload the page or contact an administrator.';
  readonly addDeviceUnavailableReason = 'Unavailable: Konobox brand not found.';

  // Role and Permissions (AC-1)
  userRole: string | null = null;
  permissions: any[] = [];

  // Devices State
  devices: KonoboxDevice[] = [];
  filteredDevices: KonoboxDevice[] = [];
  showTestMachines = false; // Hidden by default (KAN-164 / AC-E1)
  deviceSortColumn = 'deviceId';
  deviceSortDirection: 'asc' | 'desc' = 'asc';
  activeTooltipDeviceId: string | null = null;
  deviceLoadError = false;

  // Targeted Selection State (AC-4)
  selectedForReleaseDeviceIds = new Set<string>();

  // Modals & Action Menu State (AC-2, AC-4, AC-7, AC-8, AC-10)
  selectedDeviceForRelease: KonoboxDevice | null = null;
  selectedDeviceForRemoteControl: KonoboxDevice | null = null;
  showAppVersionsModal = false;
  showReleaseHistoryModal = false;
  historyModalDeviceIdFilter?: string;
  activeActionMenuDeviceId: string | null = null;

  // Telemetry configuration and cache
  pingIntervalMinutes = 15;
  pingIntervalMs = 15 * 60 * 1000;
  private rawDeviceDocs: { id: string; data: any }[] = [];
  private storeMap = new Map<string, StoreOption>();
  private hasInitializedFilters = false;

  // Dropdown Filter States (Matching Photobooth Management)
  showStoreFilterDropdown = false;
  selectedStores = new Set<string>();
  allUniqueStores: string[] = [];
  storeFilterSearchText = '';

  showDeviceFilterDropdown = false;
  selectedDevices = new Set<string>();
  allUniqueDevices: string[] = [];
  deviceFilterSearchText = '';

  // Stores List (for device store assignment)
  stores: StoreOption[] = [];
  // Stores owned by at least one Konobox company: the only stores a Konobox device can be assigned to.
  assignableStores: StoreOption[] = [];

  // Device Add/Edit Modal (AC-5)
  showDeviceModal = false;
  isEditingDevice = false;
  /** Real Firestore document id of the device being edited; never re-derived from the (upper-cased) Device ID text. */
  private editingDocId = '';
  deviceFormData = {
    deviceId: '',
    deviceName: '',
    storeId: '',
    useCash: true,
    useCard: true,
    useCoupon: false,
    isTesting: false
  };
  deviceFormError = '';
  deviceIdError = '';
  isSavingDevice = false;

  // Ping timer refresh
  private refreshInterval: any;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event?: MouseEvent): void {
    if (event) {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown')) {
        this.showStoreFilterDropdown = false;
        this.showDeviceFilterDropdown = false;
      }
      if (!target.closest('.tooltip-wrapper')) {
        this.activeTooltipDeviceId = null;
      }
      if (!target.closest('.action-dropdown')) {
        this.activeActionMenuDeviceId = null;
      }
    } else {
      this.activeTooltipDeviceId = null;
      this.activeActionMenuDeviceId = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    this.activeActionMenuDeviceId = null;
  }

  // -------------------------------------------------------------------------
  // RBAC Permission Gates (AC-1)
  // -------------------------------------------------------------------------
  canReleaseDevice(): boolean {
    if (this.userRole?.toLowerCase().trim() === 'superadmin') return true;
    const perm = this.permissions.find(p => p.id === 'konobox-management_release-device');
    return perm && perm.roles ? perm.roles.some((r: string) => r.toLowerCase().trim() === this.userRole?.toLowerCase().trim()) : false;
  }

  canReleaseAll(): boolean {
    if (this.userRole?.toLowerCase().trim() === 'superadmin') return true;
    // AC-1: A user with only release-all sees no release controls
    if (!this.canReleaseDevice()) return false;
    const perm = this.permissions.find(p => p.id === 'konobox-management_release-all');
    return perm && perm.roles ? perm.roles.some((r: string) => r.toLowerCase().trim() === this.userRole?.toLowerCase().trim()) : false;
  }

  canRemoteControl(): boolean {
    if (this.userRole?.toLowerCase().trim() === 'superadmin') return true;
    const perm = this.permissions.find(p => p.id === 'konobox-management_remote-control');
    return perm && perm.roles ? perm.roles.some((r: string) => r.toLowerCase().trim() === this.userRole?.toLowerCase().trim()) : false;
  }

  /** KAN-578: superadmin implicit; NOT implied by release-device or release-all. */
  canDeleteRelease(): boolean {
    if (this.userRole?.toLowerCase().trim() === 'superadmin') return true;
    const perm = this.permissions.find(p => p.id === 'konobox-management_release-delete');
    return perm && perm.roles ? perm.roles.some((r: string) => r.toLowerCase().trim() === this.userRole?.toLowerCase().trim()) : false;
  }

  canReleaseApp(): boolean {
    return this.selectedDeviceCount > 0
      ? (this.canReleaseDevice() || this.canReleaseAll())
      : this.canReleaseAll();
  }

  // -------------------------------------------------------------------------
  // Targeted Selection Helpers (AC-4)
  // -------------------------------------------------------------------------
  get selectedDeviceCount(): number {
    return this.selectedForReleaseDeviceIds.size;
  }

  get isAllVisibleDevicesSelected(): boolean {
    if (this.filteredDevices.length === 0) return false;
    return this.filteredDevices.every(d => this.selectedForReleaseDeviceIds.has(d.deviceId));
  }

  get isIndeterminateSelection(): boolean {
    if (this.filteredDevices.length === 0) return false;
    const count = this.filteredDevices.filter(d => this.selectedForReleaseDeviceIds.has(d.deviceId)).length;
    return count > 0 && count < this.filteredDevices.length;
  }

  toggleSelectAllVisibleDevices(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.filteredDevices.forEach(d => this.selectedForReleaseDeviceIds.add(d.deviceId));
    } else {
      this.filteredDevices.forEach(d => this.selectedForReleaseDeviceIds.delete(d.deviceId));
    }
  }

  toggleDeviceCheck(deviceId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.selectedForReleaseDeviceIds.add(deviceId);
    } else {
      this.selectedForReleaseDeviceIds.delete(deviceId);
    }
  }

  clearDeviceSelection(): void {
    this.selectedForReleaseDeviceIds.clear();
  }

  // -------------------------------------------------------------------------
  // Action Menu & Modals Management (AC-2, AC-4, AC-7, AC-8, AC-10)
  // -------------------------------------------------------------------------
  toggleActionMenu(deviceId: string, event: Event): void {
    event.stopPropagation();
    this.activeActionMenuDeviceId = this.activeActionMenuDeviceId === deviceId ? null : deviceId;
  }

  closeActionMenu(): void {
    this.activeActionMenuDeviceId = null;
  }

  openDeviceReleaseModal(device: KonoboxDevice): void {
    this.selectedDeviceForRelease = device;
  }

  closeDeviceReleaseModal(): void {
    this.selectedDeviceForRelease = null;
  }

  async onDeviceReleaseSuccess(outcome: CoinSingReleaseOutcome): Promise<void> {
    this.closeDeviceReleaseModal();
    await this.loadAllData();
  }

  openAppVersionsModal(): void {
    this.showAppVersionsModal = true;
  }

  closeAppVersionsModal(): void {
    this.showAppVersionsModal = false;
  }

  async onBulkReleaseSuccess(outcome: CoinSingReleaseOutcome): Promise<void> {
    this.clearDeviceSelection();
    await this.loadAllData();
  }

  openReleaseHistoryModal(deviceId?: string): void {
    this.historyModalDeviceIdFilter = deviceId;
    this.showReleaseHistoryModal = true;
  }

  closeReleaseHistoryModal(): void {
    this.showReleaseHistoryModal = false;
    this.historyModalDeviceIdFilter = undefined;
  }

  openRemoteControlModal(device: KonoboxDevice): void {
    this.selectedDeviceForRemoteControl = device;
  }

  closeRemoteControlModal(): void {
    this.selectedDeviceForRemoteControl = null;
  }

  getUpdatePill(device: KonoboxDevice): CoinSingPillResult {
    return computeCoinSingUpdatePill(device);
  }

  get pageBrandBanner(): string {
    if (this.brandsLoading) return '';
    if (this.brandLoadError) return this.msgBrandLoadFailed;
    return this.konoboxBrand ? '' : this.msgBrandNotFound;
  }

  get isAddDeviceDisabled(): boolean {
    return this.brandsLoading || this.brandLoadError || !this.konoboxBrand;
  }

  get addDeviceReason(): string {
    return !this.brandsLoading && !this.brandLoadError && !this.konoboxBrand ? this.addDeviceUnavailableReason : '';
  }

  async ngOnInit(): Promise<void> {
    // 0. RBAC permissions listener (AC-1)
    if (this.auth && this.accessService) {
      try {
        this.authUnsubscribe = onAuthStateChanged(this.auth, async (user) => {
          if (user && this.accessService) {
            try {
              const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
              this.userRole = profile?.role || null;
            } catch (err) {
              console.warn('Could not read user profile:', err);
            }
            this.accessService.getPagePermissions().subscribe(perms => {
              this.permissions = perms || [];
            });
          }
        });
      } catch (err) {
        console.warn('Auth state listener initialization skipped:', err);
      }
    }

    // 1. Live brand lookup: banner and disabled state clear without a reload once brand resolves.
    this.brandSub = this.brandService.getBrands().subscribe({
      next: brands => {
        const previous = this.konoboxBrand;
        this.konoboxBrand = resolveKonoboxBrand(brands);
        this.brandLoadError = false;
        const first = this.brandsLoading;
        this.brandsLoading = false;
        if (first || previous?.id !== this.konoboxBrand?.id || previous?.name !== this.konoboxBrand?.name) {
          void this.loadAllData();
        }
      },
      error: err => {
        console.error('Error loading brands:', err);
        this.brandLoadError = true;
        this.brandsLoading = false;
        this.konoboxBrand = null;
        void this.loadAllData();
      }
    });

    // 2. Real-time devices collection listener (KAN-164 / AC-B5).
    this.deviceSub = this.deviceService.watchDevices(
      docs => {
        this.rawDeviceDocs = docs;
        this.deviceLoadError = false;
        this.processDeviceDocs(docs);
      },
      err => {
        console.error('Error in watchDevices listener:', err);
        this.deviceLoadError = true;
      }
    );

    // 3. Periodic status recalculation timer (every 30 seconds, AC-B6 / R-B6).
    this.refreshInterval = setInterval(() => {
      this.recalculateDeviceStatuses();
    }, 30000);
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    if (this.deviceSub) {
      this.deviceSub();
      this.deviceSub = undefined;
    }
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = undefined;
    }
    this.brandSub?.unsubscribe();
  }

  async loadAllData(): Promise<void> {
    this.isLoading = true;
    try {
      // 1. Fetch ping interval from PhotoboothService (fallback to default 15m)
      try {
        this.pingIntervalMinutes = await this.photoboothService.getPingInterval();
        this.pingIntervalMs = normalizePingIntervalMs(this.pingIntervalMinutes);
      } catch (err) {
        console.warn('Could not read pingInterval, defaulting to 15m:', err);
        this.pingIntervalMinutes = 15;
        this.pingIntervalMs = 15 * 60 * 1000;
      }

      // 2. Konobox brand (resolved by the live subscription). Without it nothing is listed and nothing can be written.
      const brand = this.konoboxBrand;
      if (!brand?.id) {
        this.devices = [];
        this.stores = [];
        this.assignableStores = [];
        this.allUniqueStores = [];
        this.allUniqueDevices = [];
        this.selectedStores = new Set();
        this.selectedDevices = new Set();
        this.applyDeviceFilters();
        return;
      }
      const konoboxBrandId = brand.id;

      // 3. Load Stores for store mapping & assignment dropdown
      const storesSnap = await getDocs(collection(this.firestore, 'stores'));
      const storeList: StoreOption[] = [];
      const newStoreMap = new Map<string, StoreOption>();

      storesSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data['isDeleted']) return;

        const storeObj: StoreOption = {
          id: docSnap.id,
          storeId: data['storeId'] || docSnap.id,
          name: data['name'] || 'Unnamed Store',
          location: data['location'] || '',
          brandId: data['brandId'] || '',
          companyIds: Array.isArray(data['companyIds']) ? data['companyIds'] : (data['companyId'] ? [data['companyId']] : [])
        };
        storeList.push(storeObj);
        newStoreMap.set(docSnap.id, storeObj);
        if (data['storeId']) {
          newStoreMap.set(data['storeId'], storeObj);
        }
      });

      this.stores = storeList.sort((a, b) => a.name.localeCompare(b.name));
      this.storeMap = newStoreMap;

      // Only stores owned by a Konobox company can be assigned (a company belongs to Konobox by its brand id or name).
      let konoboxCompanyIds = new Set<string>();
      try {
        const companies = await firstValueFrom(this.companyService.getCompanies());
        const brandName = (brand.name || '').trim().toLowerCase();
        konoboxCompanyIds = new Set(
          companies
            .filter((c: any) => c['isDeleted'] !== true && c.id)
            .filter((c: any) => {
              const ref = String(c.brand || '').trim();
              return ref === konoboxBrandId || (!!ref && ref.toLowerCase() === brandName);
            })
            .map((c: any) => c.id as string)
        );
      } catch (err) {
        console.error('Could not load companies for the store assignment list:', err);
      }
      this.assignableStores = this.stores.filter(s => (s.companyIds || []).some(id => konoboxCompanyIds.has(id)));

      // 4. Ingest Devices
      if (this.rawDeviceDocs.length > 0) {
        this.processDeviceDocs(this.rawDeviceDocs);
      } else {
        const deviceDocs = await this.deviceService.listDeviceDocs();
        this.rawDeviceDocs = deviceDocs;
        this.processDeviceDocs(deviceDocs);
      }
    } catch (err) {
      console.error('Error loading Konobox data:', err);
    } finally {
      this.isLoading = false;
    }
  }

  processDeviceDocs(deviceDocs: { id: string; data: any }[]): void {
    const brand = this.konoboxBrand;
    if (!brand?.id) {
      this.devices = [];
      this.applyDeviceFilters();
      return;
    }
    const konoboxBrandId = brand.id;
    const now = Date.now();
    const deviceList: KonoboxDevice[] = [];

    deviceDocs.forEach(docSnap => {
      const dev = docSnap.data;
      if (docSnap.id === 'global' || dev['deviceId'] === 'global' || dev['isDeleted'] === true) {
        return;
      }

      const devBrandId = String(dev['brandId'] || '').trim();
      const storeAssoc = dev['storeDocId'] ? this.storeMap.get(dev['storeDocId']) : (dev['storeId'] ? this.storeMap.get(dev['storeId']) : null);
      // A device with a brandId must carry the Konobox brand ID exactly (legacy or unresolved values such as 'konobox'
      // are intentionally hidden, AC-C5). Only a device with no brandId is attributed through its store's brand.
      const isKonoDevice = devBrandId ? devBrandId === konoboxBrandId : (storeAssoc?.brandId || '') === konoboxBrandId;

      if (!isKonoDevice) {
        return; // Exclude PhotoLavie booths
      }

      const deviceId = dev['deviceId'] || docSnap.id;
      const statusRes = computeStatus(dev, now, this.pingIntervalMs);

      deviceList.push({
        ...dev,
        docId: docSnap.id,   // the real Firestore document id (CoinSing may have registered it in lower case)
        deviceId: deviceId,
        name: dev['name'] || dev['deviceName'] || deviceId,
        deviceName: dev['deviceName'] || dev['name'] || deviceId,
        storeId: dev['storeId'] || (storeAssoc ? storeAssoc.storeId : ''),
        storeDocId: dev['storeDocId'] || (storeAssoc ? storeAssoc.id : ''),
        storeName: storeAssoc ? storeAssoc.name : (dev['storeId'] || 'Unassigned Store'),
        storeLocation: storeAssoc ? storeAssoc.location : '',
        brandId: konoboxBrandId,
        brandName: brand.name,
        isTesting: dev['isTesting'] === true,
        isDeleted: false,
        lastSeen: dev['lastSeen'],
        lastPingedDate: statusRes.lastPingedDate,
        deviceOnline: statusRes.isOnline,
        cardMachineConnected: dev['cardMachineConnected'] === true,
        billAcceptorConnected: dev['billAcceptorConnected'] === true,
        useCard: statusRes.useCard,
        useCash: statusRes.useCash,
        useCoupon: dev['useCoupon'] === true,
        isInMaintenance: dev['isInMaintenance'] === true,
        appVersion: dev['appVersion'] || dev['currentAppVersion'] || dev['version'] || '',
        coinsingTargetVersion: dev['coinsingTargetVersion'] || dev['targetAppVersion'] || '',
        coinsingUpdateNeeded: dev['coinsingUpdateNeeded'] === true,
        coinsingUpdate: dev['coinsingUpdate'] || null,
        overallStatus: statusRes.overallStatus
      });
    });

    this.devices = deviceList;

    // AC-4: Live snapshot updates never clear the selection; a device deleted drops out with a notice.
    if (this.selectedForReleaseDeviceIds.size > 0) {
      const activeIds = new Set(deviceList.map(d => d.deviceId));
      for (const id of Array.from(this.selectedForReleaseDeviceIds)) {
        if (!activeIds.has(id)) {
          this.selectedForReleaseDeviceIds.delete(id);
          this.showToast(`Device ${id} was deleted and removed from selection.`);
        }
      }
    }

    // Extract unique store names and device IDs for dropdown filters
    const uniqueStores = new Set<string>();
    const uniqueDevs = new Set<string>();
    deviceList.forEach(d => {
      if (d.storeName) uniqueStores.add(d.storeName);
      if (d.deviceId) uniqueDevs.add(d.deviceId);
    });
    this.allUniqueStores = Array.from(uniqueStores).sort((a, b) => a.localeCompare(b));
    this.allUniqueDevices = Array.from(uniqueDevs).sort((a, b) => a.localeCompare(b));

    // Preserve active selections across live snapshot updates:
    // If uninitialized, select all. If previously all items were selected, continue selecting all so newly added devices are visible.
    if (!this.hasInitializedFilters) {
      this.selectedStores = new Set(this.allUniqueStores);
      this.selectedDevices = new Set(this.allUniqueDevices);
      this.hasInitializedFilters = true;
    } else {
      // Auto-include newly created devices if the user hasn't explicitly filtered out devices
      this.allUniqueDevices.forEach(d => {
        if (!this.selectedDevices.has(d) && this.selectedDevices.size === this.allUniqueDevices.length - 1) {
          this.selectedDevices.add(d);
        }
      });
      this.allUniqueStores.forEach(s => {
        if (!this.selectedStores.has(s) && this.selectedStores.size === this.allUniqueStores.length - 1) {
          this.selectedStores.add(s);
        }
      });
    }

    this.applyDeviceFilters(false);
  }

  recalculateDeviceStatuses(): void {
    const now = Date.now();
    this.devices.forEach(dev => {
      const res = computeStatus(dev, now, this.pingIntervalMs);
      dev.overallStatus = res.overallStatus;
      dev.deviceOnline = res.isOnline;
      dev.lastPingedDate = res.lastPingedDate;
    });
    this.applyDeviceFilters(false);
  }

  // -------------------------------------------------------------------------
  // Dropdown Filters Logic (Matching Photobooth Management)
  // -------------------------------------------------------------------------
  toggleStoreFilter(event?: Event): void {
    if (event) event.stopPropagation();
    this.showStoreFilterDropdown = !this.showStoreFilterDropdown;
    if (this.showStoreFilterDropdown) {
      this.showDeviceFilterDropdown = false;
    }
  }

  toggleDeviceFilter(event?: Event): void {
    if (event) event.stopPropagation();
    this.showDeviceFilterDropdown = !this.showDeviceFilterDropdown;
    if (this.showDeviceFilterDropdown) {
      this.showStoreFilterDropdown = false;
    }
  }

  getFilteredStores(): string[] {
    if (!this.storeFilterSearchText.trim()) return this.allUniqueStores;
    const lower = this.storeFilterSearchText.toLowerCase();
    return this.allUniqueStores.filter(s => s.toLowerCase().includes(lower));
  }

  getFilteredDevices(): string[] {
    if (!this.deviceFilterSearchText.trim()) return this.allUniqueDevices;
    const lower = this.deviceFilterSearchText.toLowerCase();
    return this.allUniqueDevices.filter(d => d.toLowerCase().includes(lower));
  }

  get isAllStoresSelected(): boolean {
    return this.allUniqueStores.length > 0 && this.selectedStores.size === this.allUniqueStores.length;
  }

  get isAllDevicesSelected(): boolean {
    return this.allUniqueDevices.length > 0 && this.selectedDevices.size === this.allUniqueDevices.length;
  }

  toggleAllStores(): void {
    if (this.isAllStoresSelected) {
      this.selectedStores.clear();
    } else {
      this.allUniqueStores.forEach(s => this.selectedStores.add(s));
    }
    this.applyDeviceFilters(true);
  }

  toggleAllDevices(): void {
    if (this.isAllDevicesSelected) {
      this.selectedDevices.clear();
    } else {
      this.allUniqueDevices.forEach(d => this.selectedDevices.add(d));
    }
    this.applyDeviceFilters(true);
  }

  toggleStoreSelection(store: string): void {
    if (this.selectedStores.has(store)) {
      this.selectedStores.delete(store);
    } else {
      this.selectedStores.add(store);
    }
    this.applyDeviceFilters(true);
  }

  toggleDeviceSelection(device: string): void {
    if (this.selectedDevices.has(device)) {
      this.selectedDevices.delete(device);
    } else {
      this.selectedDevices.add(device);
    }
    this.applyDeviceFilters(true);
  }

  // Tooltip
  toggleTooltip(deviceId: string, event: Event): void {
    event.stopPropagation();
    if (this.activeTooltipDeviceId === deviceId) {
      this.activeTooltipDeviceId = null;
    } else {
      this.activeTooltipDeviceId = deviceId;
    }
  }

  // Filter & Sort Devices
  applyDeviceFilters(isUserFilterChange: boolean = false): void {
    let list = [...this.devices];

    if (!this.showTestMachines) {
      list = list.filter(d => !d.isTesting);
    }

    if (this.selectedStores.size > 0 && this.selectedStores.size < this.allUniqueStores.length) {
      list = list.filter(d => this.selectedStores.has(d.storeName || 'Unassigned Store'));
    } else if (this.selectedStores.size === 0) {
      list = [];
    }

    if (this.selectedDevices.size > 0 && this.selectedDevices.size < this.allUniqueDevices.length) {
      list = list.filter(d => this.selectedDevices.has(d.deviceId));
    } else if (this.selectedDevices.size === 0) {
      list = [];
    }

    // Sort
    if (this.deviceSortColumn === 'lastSeen') {
      list.sort((a, b) => compareLastSeen(a, b, this.deviceSortDirection));
    } else if (this.deviceSortColumn === 'appVersion') {
      // KAN-433 AC-3: real version order; raw and empty values group last in both directions; ties by deviceId.
      list.sort((a, b) => compareInstalledVersionsForSort(a, b, this.deviceSortDirection));
    } else {
      list.sort((a, b) => {
        let valA: any = a[this.deviceSortColumn] || '';
        let valB: any = b[this.deviceSortColumn] || '';
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return this.deviceSortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.deviceSortDirection === 'asc' ? 1 : -1;
        return (a.deviceId || '').localeCompare(b.deviceId || '');
      });
    }

    this.filteredDevices = list;

    // AC-4: Selection rules: devices hidden by a filter are deselected with a toast
    if (isUserFilterChange && this.selectedForReleaseDeviceIds.size > 0) {
      const visibleSet = new Set(this.filteredDevices.map(d => d.deviceId));
      let deselectedCount = 0;
      for (const id of Array.from(this.selectedForReleaseDeviceIds)) {
        if (!visibleSet.has(id)) {
          this.selectedForReleaseDeviceIds.delete(id);
          deselectedCount++;
        }
      }
      if (deselectedCount > 0) {
        this.showToast(`${deselectedCount} selected device(s) deselected because they were filtered out.`);
      }
    }
  }

  formatLastSeen(lastSeen: any): string {
    return formatLastSeenPacific(lastSeen);
  }

  /** KAN-433 AC-3: how the App Version column and the Status tooltip show a device's reported version. */
  versionView(dev: { appVersion?: unknown }): InstalledVersionView {
    return describeInstalledVersion(dev.appVersion);
  }

  /** `aria-sort` value of a sortable header for the current sort. */
  ariaSortFor(col: string): 'ascending' | 'descending' | 'none' {
    if (this.deviceSortColumn !== col) return 'none';
    return this.deviceSortDirection === 'asc' ? 'ascending' : 'descending';
  }

  sortDevicesBy(col: string): void {
    if (this.deviceSortColumn === col) {
      this.deviceSortDirection = this.deviceSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.deviceSortColumn = col;
      this.deviceSortDirection = 'asc';
    }
    this.applyDeviceFilters();
  }

  // Device Modal Workflows (AC-5)
  openAddDeviceModal(): void {
    if (this.isAddDeviceDisabled) return;
    this.isEditingDevice = false;
    this.deviceIdError = '';
    this.editingDocId = '';
    this.deviceFormData = {
      deviceId: '',
      deviceName: '',
      storeId: this.assignableStores.length > 0 ? (this.assignableStores[0].storeId || this.assignableStores[0].id) : '',
      useCash: true,
      useCard: true,
      useCoupon: false,
      isTesting: false
    };
    this.deviceFormError = '';
    this.showDeviceModal = true;
    this.focusSoon('modalDeviceId');
  }

  openEditDeviceModal(device: KonoboxDevice): void {
    this.isEditingDevice = true;
    this.editingDocId = String(device['docId'] || device.deviceId || '');
    this.deviceFormData = {
      deviceId: device.deviceId,
      deviceName: device.deviceName || device.name || '',
      // The store select's option value is the store code (or doc id when there is no code), so match on either of the device's references.
      storeId: (() => {
        const match = this.stores.find(s => s.id === device.storeDocId || s.storeId === device.storeId || s.id === device.storeId || s.storeId === device.storeDocId);
        return match ? (match.storeId || match.id) : (device.storeId || device.storeDocId || '');
      })(),
      useCash: device.useCash !== false,
      useCard: device.useCard !== false,
      useCoupon: device.useCoupon === true,
      isTesting: device.isTesting === true
    };
    this.deviceFormError = '';
    this.showDeviceModal = true;
  }

  /** Store choices in the modal: Konobox-owned stores, plus the device's current store when editing so it never shows blank. */
  get modalStoreOptions(): StoreOption[] {
    const options = [...this.assignableStores];
    const current = this.deviceFormData.storeId;
    if (this.isEditingDevice && current && !options.some(s => (s.storeId || s.id) === current)) {
      const existing = this.stores.find(s => (s.storeId || s.id) === current);
      if (existing) options.push(existing);
    }
    return options;
  }

  closeDeviceModal(): void {
    this.showDeviceModal = false;
    this.deviceFormError = '';
    this.deviceIdError = '';
  }

  /** Device ID rule (AC-A5): trimmed, 1-64 ASCII letters, digits, '-' or '_'; GLOBAL is reserved. Returns '' when valid. */
  validateDeviceId(raw: string): string {
    const id = (raw || '').trim();
    if (!id) return 'Device ID is required.';
    if (id.length > 64) return 'Device ID must be 64 characters or fewer.';
    if (!/^[A-Za-z0-9_-]+$/.test(id)) return 'Use only letters, numbers, - and _.';
    if (id.toUpperCase() === 'GLOBAL') return 'Device ID "GLOBAL" is reserved. Use a different ID.';
    return '';
  }

  /** Trim and upper-case on blur, as the hint promises. */
  normalizeDeviceId(): void {
    if (this.isEditingDevice) return;
    this.deviceFormData.deviceId = (this.deviceFormData.deviceId || '').trim().toUpperCase();
  }

  onDeviceIdInput(): void {
    this.deviceIdError = '';
    this.deviceFormError = '';
  }

  private focusSoon(elementId: string): void {
    setTimeout(() => document.getElementById(elementId)?.focus(), 0);
  }

  private showFormError(message: string): void {
    this.deviceFormError = message;
    setTimeout(() => document.querySelector('[data-testid="km-device-form-error"]')?.scrollIntoView({ block: 'nearest' }), 0);
  }

  private showToast(message: string): void {
    this.toastMessage = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toastMessage = ''), 6000);
  }

  onDeviceModalStoreChange(): void {
    // Optional hook when store changes
  }

  async saveDeviceModal(): Promise<void> {
    if (this.isSavingDevice) return; // double click / double Enter
    const brand = this.konoboxBrand;
    if (!brand?.id) return; // no brand, nothing is written (AC-C3)

    if (!this.isEditingDevice) {
      const idError = this.validateDeviceId(this.deviceFormData.deviceId);
      if (idError) {
        this.deviceIdError = idError;
        this.deviceFormError = '';
        this.focusSoon('modalDeviceId');
        return;
      }
    }
    if (!this.deviceFormData.storeId) {
      this.showFormError('Store Assignment is required.');
      this.focusSoon('modalStoreSelect');
      return;
    }

    // New devices are saved upper-case. An existing device keeps the exact Device ID and document id it was registered
    // with (CoinSing does not upper-case, so the document may be lower-case): editing must never point at another document.
    const cleanDeviceId = this.isEditingDevice
      ? this.deviceFormData.deviceId.trim()
      : this.deviceFormData.deviceId.trim().toUpperCase();
    if (!this.isEditingDevice) this.deviceFormData.deviceId = cleanDeviceId;

    this.isSavingDevice = true;
    this.deviceFormError = '';
    this.deviceIdError = '';

    try {
      const storeAssoc = this.stores.find(s => s.storeId === this.deviceFormData.storeId || s.id === this.deviceFormData.storeId);
      const now = new Date();

      const payload: any = {
        name: this.deviceFormData.deviceName?.trim() || cleanDeviceId,
        deviceName: this.deviceFormData.deviceName?.trim() || cleanDeviceId,
        storeId: storeAssoc ? storeAssoc.storeId : this.deviceFormData.storeId,
        storeDocId: storeAssoc ? storeAssoc.id : '',
        brandId: brand.id,
        brandName: brand.name,
        isTesting: this.deviceFormData.isTesting === true,
        useCard: this.deviceFormData.useCard !== false,
        useCash: this.deviceFormData.useCash !== false,
        // KAN-229: staff-controlled; missing or false keeps the CoinSing COUPON button hidden.
        useCoupon: this.deviceFormData.useCoupon === true,
        updatedAt: now
      };

      if (!this.isEditingDevice) {
        payload.deviceId = cleanDeviceId;
        payload.isDeleted = false;
        payload.createdAt = now;
        // Firestore check + transaction (set without merge): rejects an existing ID, replaces a soft-deleted Konobox one in full.
        await this.deviceService.createDeviceIfAbsent(cleanDeviceId, payload, brand.id);
      } else {
        await updateDoc(doc(this.firestore, 'devices', this.editingDocId || cleanDeviceId), payload);
      }
    } catch (err: any) {
      console.error('Error saving device:', err);
      if (err instanceof DeviceIdTakenError) {
        this.showFormError(err.reason === 'race'
          ? 'This Device ID was just registered by someone else. Nothing was saved. Enter a different ID.'
          : `Device ID "${cleanDeviceId}" is already registered to an active device. Use a different ID or edit the existing device.`);
        this.focusSoon('modalDeviceId');
      } else if (err instanceof DeviceVerifyError || err?.code === 'permission-denied') {
        this.showFormError('Could not verify the Device ID. Check your connection or permissions and try again. Nothing was saved.');
      } else {
        this.showFormError('The device could not be saved. Nothing was changed. Try again.');
      }
      this.isSavingDevice = false;
      return;
    }

    try {
      await this.storeService.syncDeviceCounts();
    } catch (err) {
      console.error('Device saved, but store device counts could not be synced:', err);
    }
    const hiddenTestMachine = !this.isEditingDevice && this.deviceFormData.isTesting === true && !this.showTestMachines;
    this.closeDeviceModal();
    this.isSavingDevice = false;
    await this.loadAllData();
    if (hiddenTestMachine) {
      this.showToast(`Device ${cleanDeviceId} created. Hidden because Show test machines is off.`);
    }
  }

  async deleteDevice(device: KonoboxDevice): Promise<void> {
    if (!confirm(`Are you sure you want to delete Konobox device "${device.deviceId}"?`)) {
      return;
    }
    try {
      await this.storeService.deleteDevice(device.deviceId);
      await this.loadAllData();
    } catch (err) {
      console.error('Error deleting device:', err);
      alert('Failed to delete device.');
    }
  }
}
